import { FC, useEffect, useState } from "react";
import {
  AccordionHeader,
  AccordionHeaderText,
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiTable,
  MuiTypography,
  MuiViewMorePopOver,
  RowProps,
} from "../../../../components";
import { viewMorePopOverGridSize } from "../../../../helpers/constants";
import {
  getFullName,
  getValueOrBlank,
  isNotEmpty,
} from "../../../../helpers/utils";
import { useGridContainer } from "../../../../hooks/useGridContainer";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { ViewUserDataType } from "../../../createUser/modelsAndSchemas/models";
import { createUserSiteCoreKeys } from "../../../createUser/siteCore/SiteCoreKeys";
import { ViewUserPowerBiReportDetailsWrapper } from "./ViewUserPowerBiReportDetails.style";

interface ViewUserPowerBiReportDetailsProps {
  pageId: string;
  userData: ViewUserDataType;
}

const ViewUserPowerBiReportDetails: FC<ViewUserPowerBiReportDetailsProps> = ({
  pageId,
  userData,
}) => {
  const { gridSizeMedium: gridSize, gridSizeMediumClass } =
    useGridContainer(true);
  const { useFetchLabel, commonLabels, fetchDropdownValues } = useSiteCore();
  const [addedReports, setAddedReports] = useState<any[]>([]);
  const getPowerBIReportsLabel = useFetchLabel(
    createUserSiteCoreKeys.powerBIReportsKey,
  );
  const getUseRoleLabels = useFetchLabel(createUserSiteCoreKeys.userRoleKey);

  const powerBiSiteCoreLabel = {
    reportColumnHeader: getPowerBIReportsLabel(
      createUserSiteCoreKeys.reportColumnHeader,
    ),

    reports: getPowerBIReportsLabel(createUserSiteCoreKeys.reports),
    all: getPowerBIReportsLabel(createUserSiteCoreKeys.all),
  };

  useEffect(() => {
    if (userData?.reports?.powerBIReport) {
      const transformedReports = userData.reports.powerBIReport.map((r) => ({
        reportId: r.reportId,
        reportName: r.reportName,
      }));
      setAddedReports(transformedReports);
    }
  }, [userData, setAddedReports]);

  const columns = [
    {
      id: "reportName",
      label: powerBiSiteCoreLabel.reportColumnHeader,
      isSortVisible: true,
      colSpan: 3,
      render: (row: RowProps) => (
        <MuiBox>
          <MuiTypography variant="h3">{row.reportName}</MuiTypography>
        </MuiBox>
      ),
    },
  ];

  return (
    <ViewUserPowerBiReportDetailsWrapper>
      <MuiGrid container className="user-role-email-report-grid">
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getPowerBIReportsLabel(
              createUserSiteCoreKeys.userEmailReport,
            )}
            replaceEmptyWithDash
            value={userData?.emailAddress}
            isValueWithEllipsis
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getUseRoleLabels(createUserSiteCoreKeys.userName)}
            replaceEmptyWithDash
            value={getFullName(userData?.lastName, userData?.firstName, true)}
            isValueWithEllipsis
          />
        </MuiGrid>
        <MuiGrid size={gridSize} className={gridSizeMediumClass}>
          <MuiDoubleLabel
            label={getUseRoleLabels(
              createUserSiteCoreKeys.preferredNameRoleDetails,
            )}
            replaceEmptyWithDash
            value={getValueOrBlank(userData?.username)}
            isValueWithEllipsis
          />
        </MuiGrid>
      </MuiGrid>

      <MuiBox>
        <AccordionHeader
          className="accordion-header"
          title={
            <AccordionHeaderText
              label={getPowerBIReportsLabel(
                createUserSiteCoreKeys.reportAccessSection,
              )}
            />
          }
          isExpanded={true}
          showExpandCollapseIcon={false}
        >
          <MuiBox className="accordion-container">
            <MuiBox className="reports-table">
              <MuiTable
                pageId={pageId}
                tableHeaderForm={[]}
                columns={columns}
                tableResults={addedReports}
                noDataInApi={addedReports.length === 0}
                noResultMessage={commonLabels.noResult}
                emptyMessage={getPowerBIReportsLabel(
                  createUserSiteCoreKeys.noReportsFoundMessage,
                )}
                priorityResults={[]}
                isFooterVisible={true}
              />
            </MuiBox>

            <MuiGrid
              size={viewMorePopOverGridSize}
              className={gridSizeMediumClass}
            >
              <MuiDoubleLabel
                label={getPowerBIReportsLabel(createUserSiteCoreKeys.comment)}
                replaceEmptyWithDash
                value={
                  isNotEmpty(userData?.reports?.comment) ? (
                    <MuiViewMorePopOver
                      anchorText={commonLabels.viewMore}
                      message={getValueOrBlank(userData?.reports?.comment)}
                      placement="bottom-end"
                    />
                  ) : (
                    ""
                  )
                }
              />
            </MuiGrid>
          </MuiBox>
        </AccordionHeader>
      </MuiBox>
    </ViewUserPowerBiReportDetailsWrapper>
  );
};

export default ViewUserPowerBiReportDetails;
